import {
  ButtonProps,
  ImageProps,
  mapButtonProps,
  mapImageProps,
  mapPictureToProps,
  mapUrl,
  PropsWithId,
  TagUpdatedProps,
  VideoProps,
} from '@vk-tech/mark-ui';

import { PopupBannerCommonProps } from '~containers/PopupResolver/PopupBanner/PopupBanner.types';
import { PopupCardCommonProps } from '~containers/PopupResolver/PopupCard/PopupCard.types';
import { PopupPictureCommonProps } from '~containers/PopupResolver/PopupPicture/PopupPicture.types';

export type PopupContentProps =
  | Omit<PopupCardCommonProps, 'onClose'>
  | Omit<PopupBannerCommonProps, 'onClose'>
  | Omit<PopupPictureCommonProps, 'onClose'>;

type StrapiDataAttributes = {
  name?: string;
  value?: string;
};

type StrapiApiAction = {
  documentId?: string;
  title?: string;
  data_attributes?: StrapiDataAttributes[];
};

type StrapiButton = {
  id?: number;
  text?: string;
  api_form_label?: string;
  icon_start?: any;
  icon_end?: any;
  api_action?: StrapiApiAction;
  url?: any;
  mod?: string;
};

type StrapiImageSettings = {
  object_position?: 'top' | 'bottom' | 'center';
  object_fit?: 'cover' | 'contain';
};

type StrapiPopupCard = {
  __component: 'popup-action.card';
  id?: number;
  heading?: string;
  text?: string;
  logo_first?: any;
  logo_second?: any;
  labels?: any[];
  promocode?: string;
  buttons?: StrapiButton[];
  date?: string;
  time?: string;
  location?: string;
  timezone?: string;
  is_reversed?: boolean;
  disclaimer?: string;
  image?: any;
  image_settings?: StrapiImageSettings;
  video?: any;
  card_size?: 's' | 'm';
  overlay_mode?: 'light' | 'dark' | 'grey' | 'transparent' | 'gradient';
  is_overlay_opaque?: boolean;
  is_visible_button_close?: boolean;
  is_overlay_close?: boolean;
  promocode_api_action?: StrapiApiAction;
  scroll_lock?: boolean;
  popup_mod?: 'center' | 'bottom';
};

type StrapiPopupBanner = {
  __component: 'popup-action.banner';
  id?: number;
  heading?: string;
  text?: string;
  buttons?: StrapiButton[];
  is_reversed?: boolean;
  image?: any;
  video?: any;
  image_settings?: StrapiImageSettings;
  is_visible_button_close?: boolean;
  is_overlay_opaque?: boolean;
  is_overlay_close?: boolean;
};

type StrapiPopupPicture = {
  __component: 'popup-action.picture';
  id?: number;
  image?: any;
  url?: any;
  video?: any;
  api_action?: StrapiApiAction;
  is_overlay_opaque?: boolean;
  is_visible_button_close?: boolean;
  is_overlay_close?: boolean;
  overlay_mode?: 'light' | 'dark' | 'grey' | 'transparent' | 'gradient';
  scroll_lock?: boolean;
  popup_mod?: 'center' | 'bottom';
};

type StrapiPopupBlock =
  | StrapiPopupCard
  | StrapiPopupBanner
  | StrapiPopupPicture;

export type StrapiPopupItem = {
  id?: number;
  title?: string;
  documentId?: string;
  date_start?: string;
  date_end?: string;
  segment?: 'all' | 'new' | 'returned';
  max_shows?: number;
  delay?: number;
  is_active?: boolean;
  popup_action?: {
    documentId?: string;
    title?: string;
    block?: StrapiPopupBlock[];
    show_everywhere?: boolean;
    show_on_homepage?: boolean;
    url_whitelist?: Array<{ url: string }>;
    url_blacklist?: Array<{ url: string }>;
  };
};

export type MappedPopupAction = {
  id: string;
  maxShows: number;
  delay: number;
  segment: 'all' | 'new' | 'returned';
  allTargetActions: string[];
  props: PopupContentProps;
};

function extractApiActions(
  apiAction?: StrapiApiAction,
): Record<string, string> {
  if (!apiAction?.data_attributes?.length) return {};

  return apiAction.data_attributes.reduce(
    (acc: Record<string, string>, attr: StrapiDataAttributes) => {
      if (attr.name && attr.value) {
        acc[attr.name] = attr.value;
      }

      return acc;
    },
    {} as Record<string, string>,
  );
}

function hasTargetAction(apiAction?: StrapiApiAction): boolean {
  if (!apiAction?.data_attributes?.length) return false;

  return apiAction.data_attributes.some(
    (attr) =>
      attr.name === 'target-action' && attr.value && attr.value !== 'false',
  );
}

function mapOverlayMode(
  mode?: 'light' | 'dark' | 'grey' | 'transparent' | 'gradient',
): 'light' | 'dark' | 'grey' | 'transparent' | 'gradient' {
  return mode ?? 'dark';
}

function mapVideoProps(data: any): VideoProps | undefined {
  if (!data) return undefined;

  return {
    alt: data?.name,
    src: data?.url || '',
    type: data?.mime || '',
  };
}

export function mapPopupActionProps(
  item: StrapiPopupItem,
): MappedPopupAction | null {
  const popupAction = item.popup_action;
  const documentId = popupAction?.documentId;

  if (!documentId || !popupAction) return null;

  const block = popupAction.block?.[0];

  if (!block) return null;

  const allTargetActions: string[] = [];
  const commonProps = {
    isVisibleButtonClose: block.is_visible_button_close ?? true,
    isOverlayClose: block.is_overlay_close ?? true,
    opaque: block.is_overlay_opaque ?? false,
    mod: mapOverlayMode(
      'overlay_mode' in block
        ? (block as StrapiPopupCard | StrapiPopupPicture).overlay_mode
        : undefined,
    ),
  };

  switch (block.__component) {
    case 'popup-action.card': {
      const card = block as StrapiPopupCard;

      const buttons: PropsWithId<
        ButtonProps & { apiActions?: Record<string, string> }
      >[] = (card.buttons ?? []).map((btn) => {
        const mapped = mapButtonProps(btn as any) as PropsWithId<
          ButtonProps & {
            id?: string | number;
            apiActions?: Record<string, string>;
          }
        >;
        const apiActions = extractApiActions(btn.api_action);
        const buttonId = btn.id != null ? String(btn.id) : '';

        if (hasTargetAction(btn.api_action) && buttonId) {
          apiActions['target-action'] = buttonId;
          allTargetActions.push(buttonId);
        } else {
          delete apiActions['target-action'];
        }

        return {
          ...mapped,
          id: buttonId,
          apiActions,
        };
      });

      const promocodeApiActions = extractApiActions(card.promocode_api_action);

      if (hasTargetAction(card.promocode_api_action)) {
        promocodeApiActions['target-action'] = 'promocode';
        allTargetActions.push('promocode');
      } else {
        delete promocodeApiActions['target-action'];
      }

      const cardProps: PopupContentProps = {
        type: 'card',
        ...commonProps,
        heading: card.heading ?? '',
        text: card.text ?? '',
        buttons,
        logoFirst: card.logo_first
          ? (mapImageProps(card.logo_first) as ImageProps)
          : { src: '' },
        logoSecond: card.logo_second
          ? (mapImageProps(card.logo_second) as ImageProps)
          : undefined,
        labels: card.labels as PropsWithId<TagUpdatedProps>[] | undefined,
        promocode: card.promocode,
        promocodeApiActions,
        date: card.date ? new Date(card.date) : undefined,
        location: card.location,
        timezone: card.timezone,
        isReversed: card.is_reversed,
        disclaimer: card.disclaimer,
        image: card.image ? mapPictureToProps(card.image) : undefined,
        video: mapVideoProps(card.video),
        imageSettings: card.image_settings
          ? {
              position: card.image_settings.object_position,
              objectFit: card.image_settings.object_fit,
            }
          : undefined,
        cardSize: card.card_size,
        scrollLock: card.scroll_lock,
        popupMod: card.popup_mod,
      };

      return {
        id: documentId,
        maxShows: item.max_shows ?? 1,
        delay: item.delay ?? 5000,
        segment: item.segment ?? 'all',
        allTargetActions: Array.from(new Set(allTargetActions)),
        props: cardProps,
      };
    }

    case 'popup-action.banner': {
      const banner = block as StrapiPopupBanner;

      const buttons: PropsWithId<
        ButtonProps & { apiActions?: Record<string, string> }
      >[] = (banner.buttons ?? []).map((btn) => {
        const mapped = mapButtonProps(btn as any) as PropsWithId<
          ButtonProps & {
            id?: string | number;
            apiActions?: Record<string, string>;
          }
        >;
        const apiActions = extractApiActions(btn.api_action);
        const buttonId = btn.id != null ? String(btn.id) : '';

        if (hasTargetAction(btn.api_action) && buttonId) {
          apiActions['target-action'] = buttonId;
          allTargetActions.push(buttonId);
        } else {
          delete apiActions['target-action'];
        }

        return {
          ...mapped,
          id: buttonId,
          apiActions,
        };
      });

      const bannerProps: PopupContentProps = {
        type: 'banner',
        ...commonProps,
        heading: banner.heading ?? '',
        text: banner.text,
        buttons,
        isReversed: banner.is_reversed,
        image: banner.image ? mapPictureToProps(banner.image) : undefined,
        video: mapVideoProps(banner.video),
        imageSettings: banner.image_settings
          ? {
              position: banner.image_settings.object_position,
              objectFit: banner.image_settings.object_fit,
            }
          : undefined,
      };

      return {
        id: documentId,
        maxShows: item.max_shows ?? 1,
        delay: item.delay ?? 5000,
        segment: item.segment ?? 'all',
        allTargetActions: Array.from(new Set(allTargetActions)),
        props: bannerProps,
      };
    }

    case 'popup-action.picture': {
      const picture = block as StrapiPopupPicture;

      const apiActions = extractApiActions(picture.api_action);

      if (hasTargetAction(picture.api_action)) {
        apiActions['target-action'] = 'picture';
        allTargetActions.push('picture');
      }

      const urlData = picture.url ? mapUrl(picture.url) : undefined;

      const pictureProps: PopupContentProps = {
        type: 'picture',
        ...commonProps,
        image: picture.image ? mapPictureToProps(picture.image) : undefined,
        video: mapVideoProps(picture.video),
        url: urlData?.url,
        target: urlData?.target,
        apiAction: apiActions,
        scrollLock: picture.scroll_lock,
        popupMod: picture.popup_mod,
      };

      return {
        id: documentId,
        maxShows: item.max_shows ?? 1,
        delay: item.delay ?? 5000,
        segment: item.segment ?? 'all',
        allTargetActions: Array.from(new Set(allTargetActions)),
        props: pictureProps,
      };
    }

    default:
      return null;
  }
}
